import wcShared from '@web-components/shared';

import { Challenge } from './constants';

interface IncomingCallChallengeData {
  phones: {
    match: string;
    others: {
      [key: string]: string;
    };
  };
}

export interface ChallengeSuccessResponse {
  result: 'success';
  data: {
    uuid: string | null;
    allowed_challenges: Challenge[];
  } & (
    | { current_challenge: null }
    | {
        current_challenge: 'incoming_call';
        data: IncomingCallChallengeData;
        expire_at: string;
      }
    | {
        current_challenge: 'sms';
        data: { phone: string };
        expire_at: string;
        pattern?: string;
      }
  );
}

export interface ChallengeErrorResponse {
  result: 'error';
  error: ChallengeErrorRateLimited | ChallengeErrorInvalidPassword;
}

export interface ChallengeErrorRateLimited {
  code: 'too_many_request';
  description?: string;
  context: Partial<{
    limit: number;
    remaining: 0;
    retry_after: number;
    retry_at: number;
  }>;
}
export interface ChallengeErrorInvalidPassword {
  code: 'invalid_user_password';
  description?: string;
}

type ChallengeResponse = ChallengeSuccessResponse | ChallengeErrorResponse;

export const shieldSdk = {
  /** Create a new challenge */
  createChallenge: async (payload: { action_slug: string; phone?: string; locale?: string }) => {
    const response = await wcShared?.httpCall(
      'POST',
      '/api/web-components/1/shield/challenge',
      payload,
    );
    return (await response.json()) as ChallengeResponse;
  },

  /** Get the current challenge and it's associate data */
  getChallenge: async (uuid: string) => {
    const response = await wcShared?.httpCall(
      'GET',
      `/api/web-components/1/shield/challenge/${uuid}`,
    );
    return (await response.json()) as ChallengeResponse;
  },

  /** Change the current challenge */
  putChallenge: async (uuid: string, challenge: Challenge) => {
    const response = await wcShared?.httpCall(
      'PUT',
      `/api/web-components/1/shield/challenge/${uuid}`,
      { challenge_type: challenge },
    );
    return (await response.json()) as ChallengeResponse;
  },

  /** Resolve the current challenge */
  postChallenge: async (uuid: string, payload = {} as object) => {
    const response = await wcShared?.httpCall(
      'POST',
      `/api/web-components/1/shield/challenge/${uuid}`,
      payload,
    );
    return (await response.json()) as { result: 'success'; data: boolean } | ChallengeErrorResponse;
  },

  /** Re-send the current challenge */
  patchChallenge: async (uuid: string, payload = {} as object) => {
    const response = await wcShared?.httpCall(
      'PATCH',
      `/api/web-components/1/shield/challenge/${uuid}`,
      payload,
    );
    return (await response.json()) as ChallengeResponse;
  },

  /** Check if a challenge is solved */
  isChallengeSolved: async (uuid: string) => {
    const response = await wcShared?.httpCall(
      'GET',
      `/api/web-components/1/shield/challenge/${uuid}/solved`,
    );
    if (response.ok) {
      const json = await response.json();
      return json as { result: 'success'; data: boolean };
    }

    throw new Error('An error occured trying to GET shield challenge status', {
      cause: response.text(),
    });
  },
};
